import { Dispatch } from 'redux'
import { batch } from 'react-redux'
import {
  PusherGrammarFeedback,
  PusherParaphraseFeedback,
  ToastType,
  WtPusherEvent,
} from '@chegg-wt/core/state/paraphraser/types'
import { FeedbackType } from '@chegg-wt/core/types/paraphraser'
import {
  notifyToast,
  setActiveFeedbackTab,
  setFeedbacks,
  setIsPaperCheckInProgress,
  setIsTimedOut,
  setPaperRequiresRecheck,
  updatePaperHandler,
  updateTotalFeedbackCount,
} from '@chegg-wt/core/state/paraphraser/actions'
import { FeedbackTab, FULL_CHECK_TIMEOUT } from '@chegg-wt/core/constants/paraphraser'
import {
  handlePusherGrammarEvent,
  handlePusherParaphraseEvent,
} from '@chegg-wt/core/state/paraphraser/utils'
import { clearAllUnderlines } from '../helpers'
import { newFeedbacksOnEditHandler } from './newFeedbacksOnEditHandler'

class FullPaperChecker {
  private requiredFeedbacks: number = 0

  private gotFeedbacks: number = 0

  private gotSentences: boolean = false

  private feedbackList: WtPusherEvent[] = []

  private timeout = null

  private hasGrammarError = false

  private hasClarityError = false

  private done = () => {}

  private setDefaults = () => {
    this.timeout = null
    this.feedbackList = []
    this.gotSentences = false
    this.gotFeedbacks = 0
    this.requiredFeedbacks = 0
    this.hasClarityError = false
    this.hasGrammarError = false
  }

  private notifyErrors(eventData, dispatch: Dispatch) {
    if (!eventData.error) return

    const { feedbackType } = eventData.error

    // notify grammar error only if it is already not set
    if (!this.hasGrammarError && feedbackType === FeedbackType.GRAMMAR) {
      this.hasGrammarError = true
      dispatch(notifyToast(ToastType.GRAMMAR_ERROR))
    }
    if (!this.hasClarityError && feedbackType === FeedbackType.PARAPHRASE) {
      this.hasClarityError = true
      dispatch(notifyToast(ToastType.CLARITY_ERROR))
    }
  }

  private handleFeedbackData = (eventData: WtPusherEvent, dispatch: Dispatch) => {
    this.gotFeedbacks += 1

    this.notifyErrors(eventData, dispatch)
    // Checking eventData.feedback as not getting it in error cases
    if (eventData.feedback) {
      switch (eventData.feedback.feedbackType) {
        case FeedbackType.PARAPHRASE: {
          handlePusherParaphraseEvent(
            eventData as WtPusherEvent<PusherParaphraseFeedback>
          )
          break
        }
        case FeedbackType.GRAMMAR: {
          handlePusherGrammarEvent(eventData as WtPusherEvent<PusherGrammarFeedback>)
          break
        }
        default:
      }
    }
    this.checkCompletion(dispatch)
  }

  private checkCompletion = (dispatch: Dispatch) => {
    // We got the required number of sentences
    if (this.gotFeedbacks === this.requiredFeedbacks) {
      clearTimeout(this.timeout)
      batch(() => {
        dispatch(setIsPaperCheckInProgress(false))
        dispatch(updateTotalFeedbackCount())
        dispatch(setPaperRequiresRecheck(false))
        dispatch(updatePaperHandler())
      })
      this.setDefaults()
      dispatch(setActiveFeedbackTab(FeedbackTab.GRAMMAR_TAB))
      this.done?.()
    }
  }

  public startFullPaperCheck = (dispatch: Dispatch, done: () => void) => {
    // Remove of old timeout and data
    clearTimeout(this.timeout)
    this.done?.()
    this.setDefaults()

    this.done = done

    // Update it to max so that we never reach all sentences complete till we get the sentences
    this.requiredFeedbacks = Number.MAX_SAFE_INTEGER

    // Clear all previous data from our states and start loading
    newFeedbacksOnEditHandler.clearInternalState(dispatch)
    batch(() => {
      dispatch(setFeedbacks([]))
      dispatch(setIsPaperCheckInProgress(true))
      dispatch(setIsTimedOut(false))
    })
    clearAllUnderlines()

    this.timeout = setTimeout(() => {
      dispatch(setIsPaperCheckInProgress(false))
      this.setDefaults()

      dispatch(notifyToast(ToastType.GRAMMAR_ERROR))
      done()
    }, FULL_CHECK_TIMEOUT)
  }

  public notifyGetSentences = (sentencesLength: number, dispatch: Dispatch) => {
    // This if condition will fail if sentence comes after timeout
    if (this.requiredFeedbacks === Number.MAX_SAFE_INTEGER)
      this.requiredFeedbacks = 2 * sentencesLength
    this.gotFeedbacks = 0
    this.gotSentences = true
    this.feedbackList.forEach((feedbackData) =>
      this.handleFeedbackData(feedbackData, dispatch)
    )
  }

  public notifyErrorInPaperCheckCall = (dispatch: Dispatch) => {
    clearTimeout(this.timeout)
    batch(() => {
      dispatch(notifyToast(ToastType.GRAMMAR_ERROR))
      dispatch(setIsPaperCheckInProgress(false))
      dispatch(setPaperRequiresRecheck(true))
      dispatch(updatePaperHandler())
    })
    this.setDefaults()
    this.done?.()
  }

  public handleFeedbackEvent = (event: WtPusherEvent, dispatch: Dispatch): void => {
    // We are not expecting any sentences, these may be some old feedbacks which reached now
    if (this.requiredFeedbacks === 0) return

    // Discard old events
    if (new Date().getTime() - new Date(event.initiatedAt).getTime() > FULL_CHECK_TIMEOUT)
      return

    if (!this.gotSentences) {
      // Didn't have sentences till now, so will not be able to sort the feedbacks, adding them to temporary list
      this.feedbackList.push(event)
      return
    }
    this.handleFeedbackData(event, dispatch)
  }
}

const fullPaperChecker = new FullPaperChecker()
export default fullPaperChecker
